/* Rhody Reviews api/v1 -- customers domain. PURE EXTRACTION from lib/api.ts (gate-prep split); no behavior change. */
import { request } from './_http';
import type { Review } from './reviews';

export interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  relationship_type: string;
  do_not_contact: boolean;
  created_date: string;
  /* Real count of matched reviews this customer has left (review-join on
     GET /customers). 0 = has not left a review yet. */
  reviews_left: number;
}

export interface CustomersResponse {
  client_id: string;
  count: number;
  /* How many customers have left at least one review, + the rate
     (reviewed_count / count, 0..1). Real values from the review-join. */
  reviewed_count: number;
  review_rate: number;
  customers: Customer[];
}

export type TransactionEventType =
  | 'slot_locked'
  | 'card_mailed'
  | 'campaign_completed'
  | 'service_completed'
  | 'job_completed'
  | 'visit_logged'
  | 'custom';

export interface TransactionInput {
  event_type: TransactionEventType;
  event_date: string; // YYYY-MM-DD
  customer_id?: string;
  customer?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
  };
  notes?: string;
}

export interface TransactionCreated {
  client_id: string;
  id: string;
}

/* -------------------------------------------------------------------
   Add-customer / CSV-import / create-request shapes.
   ------------------------------------------------------------------- */
export interface CustomerInput {
  first_name?: string;
  last_name?: string;
  /* CSV convenience: a single "name" the server splits on first space. */
  name?: string;
  email?: string;
  phone?: string;
  relationship_type?: string;
  notes?: string;
}

/* POST /api/v1/customers -> the new (or upserted) customer id. */
export interface CustomerCreated {
  client_id: string;
  id: string;
}

/* PATCH /api/v1/customers/<id> -> the updated do_not_contact flag. */
export interface CustomerDncResult {
  client_id: string;
  id: string;
  do_not_contact: boolean;
}

/* POST /api/v1/customers/import -> bulk result. skipped lists per-row
   reasons (capped server-side); imported is how many landed. */
export interface CustomerImportResult {
  client_id: string;
  imported: number;
  skipped_count: number;
  skipped: { row: number; reason: string }[];
  ids: string[];
}

/* POST /api/v1/customers/reactivation-enroll -> Track B enrollment result.
   Enrolls a CSV of PAST customers into the reactivation playbook (2 SMS + 1
   email cadence). HARD-GATED on the consent attestation server-side (no
   attestation -> 403, nothing created). channel_scope + sms_enabled report the
   real server-side A2P gate (SMS stays off until Twilio A2P is approved).
   audit_recorded is whether the per-upload retention ledger row was written. */
export interface ReactivationEnrollInput {
  customers: CustomerInput[];
  consent_attested: boolean;
  attestation_text: string;
  attestation_text_version?: string;
}
export interface ReactivationEnrollResult {
  client_id: string;
  import_batch_id: string;
  imported: number;
  enrolled: number;
  skipped_count: number;
  skipped: { row: number; reason: string }[];
  channel_scope: string;
  sms_enabled: boolean;
  audit_recorded: boolean;
}

/* -------------------------------------------------------------------
   Single-customer activity timeline -- GET /api/v1/customers/{id}/activity.
   Matches customer_activity() in engine/api/v1/app.py. One time-ordered
   feed (newest first) of the customer's transactions, review requests, and
   reviews. Each event carries `type` + type-specific fields. Empty history
   returns an empty events list -- never invented. A cross-tenant/unknown id
   yields a 404 (ApiError status 404).
   ------------------------------------------------------------------- */
export interface ActivityCustomer {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  relationship_type: string | null;
  do_not_contact: boolean | null;
}

export interface ActivityCounts {
  transactions: number;
  requests: number;
  reviews: number;
}

/* Discriminated union on `type`. `at` is the event's ISO timestamp (may be
   null/empty when the source row had no date -- sorts last server-side). */
export interface TransactionActivityEvent {
  type: 'transaction';
  at: string | null;
  id: string;
  event_type: string | null;
  notes: string | null;
}
export interface RequestActivityEvent {
  type: 'request';
  at: string | null;
  id: string;
  channel: string | null;
  status: string | null;
}
export interface ReviewActivityEvent {
  type: 'review';
  at: string | null;
  id: string;
  platform: Review['platform'] | string | null;
  rating: number | null;
  review_text: string | null;
  replied: boolean;
}
export type ActivityEvent =
  | TransactionActivityEvent
  | RequestActivityEvent
  | ReviewActivityEvent;

export interface CustomerActivityResponse {
  client_id: string;
  customer_id: string;
  customer: ActivityCustomer;
  counts: ActivityCounts;
  events: ActivityEvent[];
}

export const customersApi = {
  listCustomers(
    params: { limit?: number } = {},
    signal?: AbortSignal,
  ): Promise<CustomersResponse> {
    return request<CustomersResponse>('/api/v1/customers', { query: params, signal });
  },

  createTransaction(input: TransactionInput): Promise<TransactionCreated> {
    return request<TransactionCreated>('/api/v1/transactions', {
      method: 'POST',
      body: input,
    });
  },

  /* Add a single customer for the signed-in client. Persists to the DB
     (upsert on phone/email), scoped to the token's client_id. */
  createCustomer(input: CustomerInput): Promise<CustomerCreated> {
    return request<CustomerCreated>('/api/v1/customers', {
      method: 'POST',
      body: input,
    });
  },

  /* Bulk-import parsed CSV rows. Each row is validated + upserted server-side
     and scoped to the token's client_id. Returns imported/skipped counts. */
  importCustomers(rows: CustomerInput[]): Promise<CustomerImportResult> {
    return request<CustomerImportResult>('/api/v1/customers/import', {
      method: 'POST',
      body: { customers: rows },
    });
  },

  /* Enroll a CSV of PAST customers into the reactivation playbook (Track B).
     HARD-GATED on the consent attestation server-side: without
     consent_attested:true the server returns 403 and creates nothing. Creates
     consent-stamped past_customer_import transactions the RequestAgent's Track B
     cadence consumes, plus a per-upload audit row. */
  reactivationEnroll(input: ReactivationEnrollInput): Promise<ReactivationEnrollResult> {
    return request<ReactivationEnrollResult>('/api/v1/customers/reactivation-enroll', {
      method: 'POST',
      body: input,
    });
  },

  /* Set/clear a customer's do_not_contact suppression flag. PATCH
     /api/v1/customers/<id>. Tenant-scoped (the server uses the token's
     client_id); true permanently excludes the customer from ALL future review
     requests, false restores them. Writes only the flag -- sends nothing. */
  setCustomerDoNotContact(
    customerId: string,
    doNotContact: boolean,
  ): Promise<CustomerDncResult> {
    return request<CustomerDncResult>(
      `/api/v1/customers/${encodeURIComponent(customerId)}`,
      { method: 'PATCH', body: { do_not_contact: doNotContact } },
    );
  },

  /* A single customer's activity timeline (transactions/requests/reviews),
     newest first, for the profile modal. A cross-tenant/unknown id 404s. */
  getCustomerActivity(
    customerId: string,
    signal?: AbortSignal,
  ): Promise<CustomerActivityResponse> {
    return request<CustomerActivityResponse>(
      `/api/v1/customers/${encodeURIComponent(customerId)}/activity`,
      { signal },
    );
  },
};
